import { Link } from "react-router-dom";
import { Meta } from "../components/Meta";
import { routes } from "../config/routes";
import { siteConfig } from "../config/site";

interface PageHeroProps {
  eyebrow: string;
  title: string;
  lead: string;
}

function PageHero({ eyebrow, title, lead }: PageHeroProps) {
  return (
    <header className="page-hero">
      <div className="page-hero-inner">
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p className="lead">{lead}</p>
      </div>
    </header>
  );
}

export function AboutPage() {
  return (
    <main id="main">
      <Meta
        title="About | Beautify Ourselves"
        description="Learn about the clear, considered beauty shopping experience Beautify Ourselves is creating."
        path={routes.about}
      />
      <PageHero
        eyebrow="About us"
        title="Beauty shopping, made more human."
        lead="Beautify Ourselves is being built around a simple idea: present beauty products clearly, carefully, and without inflated promises."
      />
      <article className="content-page">
        <h2>A considered beauty collection</h2>
        <p>
          Beautify Ourselves brings together beauty essentials in a setting that feels warm,
          calm and easy to explore. The preview catalogue is visible now so you can browse
          names, pack sizes and preview prices. Orders are not currently available.
        </p>
        <p>
          We believe useful information matters. Product pages show verified ingredients and
          practical guidance where those details have been confirmed, without unnecessary
          noise or exaggerated promises.
        </p>
        <h2>What matters to us</h2>
        <div className="values-grid">
          <div>
            <h3>Clarity</h3>
            <p>Product details written to be useful, readable and easy to understand.</p>
          </div>
          <div>
            <h3>Consideration</h3>
            <p>A focused collection presented with care rather than an overwhelming catalogue.</p>
          </div>
          <div>
            <h3>Confidence</h3>
            <p>Straightforward availability, delivery and customer-care information.</p>
          </div>
        </div>
      </article>
    </main>
  );
}

export function DeliveryReturnsPage() {
  return (
    <main id="main">
      <Meta
        title="Delivery & Returns | Beautify Ourselves"
        description="Current delivery and returns information for the Beautify Ourselves preview catalogue. Orders are not available."
        path={routes.delivery}
      />
      <PageHero
        eyebrow="Delivery & Returns"
        title="Delivery, made clear."
        lead="This page only states what is confirmed today. Destinations, charges and times will be published before anyone can place an order."
      />
      <article className="content-page">
        <p className="policy-meta">Last updated: 24 September 2026</p>
        <h2>Ordering status</h2>
        <p>
          Beautify Ourselves is currently a catalogue preview. Orders and payments are not
          available. Product prices shown are preview prices and may change before ordering
          opens. Delivery is charged separately from the product price.
        </p>
        <h2>United Kingdom</h2>
        <dl className="definition-list">
          <div className="definition-row">
            <dt>Delivery</dt>
            <dd>{siteConfig.delivery.uk.notice}</dd>
          </div>
          <div className="definition-row">
            <dt>Delivery charge</dt>
            <dd>{siteConfig.delivery.uk.shipping}</dd>
          </div>
        </dl>
        <h2>Republic of Ireland</h2>
        <dl className="definition-list">
          <div className="definition-row">
            <dt>Ordering</dt>
            <dd>{siteConfig.delivery.ireland.notice}</dd>
          </div>
          <div className="definition-row">
            <dt>Delivery</dt>
            <dd>{siteConfig.delivery.ireland.shipping}</dd>
          </div>
        </dl>
        <h2>Cancellations, returns and refunds</h2>
        <p>
          Because orders are not currently accepted, there are no Beautify Ourselves purchases
          to cancel or return. The cancellation, return and refund process — including any
          contact method and return address — will be published here before ordering opens.
        </p>
        <p>Customer rights under applicable law will apply when purchases become available.</p>
      </article>
    </main>
  );
}

export function FaqPage() {
  return (
    <main id="main">
      <Meta
        title="FAQ | Beautify Ourselves"
        description="Answers about the Beautify Ourselves collection, ordering, delivery, returns and customer care."
        path={routes.faq}
      />
      <PageHero
        eyebrow="Frequently asked questions"
        title="Clear answers, from the start."
        lead="Useful information about the collection and the shopping experience."
      />
      <div className="content-page">
        <details open>
          <summary>When can I order?</summary>
          <p>Orders are not currently available. You can browse the preview catalogue, but nothing can be purchased yet.</p>
        </details>
        <details>
          <summary>What is a preview price?</summary>
          <p>
            A preview price is the current displayed GBP amount for a listing. It may change
            before ordering opens. It is not a discount, a bundle saving or a confirmed sale
            price, and it does not include delivery.
          </p>
        </details>
        <details>
          <summary>Where will you deliver?</summary>
          <p>
            Delivery information is provided separately for the United Kingdom and Republic
            of Ireland. Available options will be shown before checkout when ordering opens.
          </p>
        </details>
        <details>
          <summary>How long will UK delivery take?</summary>
          <p>{siteConfig.delivery.uk.notice}</p>
        </details>
        <details>
          <summary>How long will Republic of Ireland delivery take?</summary>
          <p>{siteConfig.delivery.ireland.notice}</p>
        </details>
        <details>
          <summary>What is your returns policy?</summary>
          <p>Returns information will be published before ordering opens.</p>
        </details>
        <details>
          <summary>How can I contact Beautify Ourselves?</summary>
          <p>Customer care details will be published when the collection opens for ordering.</p>
        </details>
        <details>
          <summary>Are there terms for using this website?</summary>
          <p>
            Yes. The current{" "}
            <Link className="text-link" to={routes.terms}>
              terms of use
            </Link>{" "}
            apply to browsing this preview site. Terms of sale will be published before
            ordering opens.
          </p>
        </details>
      </div>
    </main>
  );
}

export function ContactPage() {
  return (
    <main id="main">
      <Meta
        title="Contact | Beautify Ourselves"
        description="Customer care information for Beautify Ourselves."
        path="/pages/contact.html"
        noIndex
      />
      <PageHero
        eyebrow="Customer care"
        title="We’ll be here to help."
        lead="Customer care will be available when the Beautify Ourselves collection opens for ordering."
      />
      <div className="content-page">
        <p>No orders are currently available. Customer care details will appear here before the shop opens.</p>
        <Link className="text-link" to={routes.shop}>
          Browse the collection <span aria-hidden="true">→</span>
        </Link>
      </div>
    </main>
  );
}

export function TermsPage() {
  return (
    <main id="main">
      <Meta
        title="Terms of use | Beautify Ourselves"
        description="Terms for browsing the Beautify Ourselves preview website. Orders and payments are not available."
        path={routes.terms}
      />
      <PageHero
        eyebrow="Terms of use"
        title="Using this preview website"
        lead="These terms apply to browsing Beautify Ourselves today. They are not terms of sale."
      />
      <article className="content-page">
        <p className="policy-meta">Last updated: 24 September 2026</p>
        <h2>Who publishes this website</h2>
        <p>
          This website is published under the name Beautify Ourselves at
          beautifyourselves.com. The legal business name, registered number and postal
          address have not been published yet. Customer-care contact details will appear
          here when they are confirmed.
        </p>
        <h2>About this website</h2>
        <p>
          This website currently displays a preview of a planned beauty catalogue. Products
          cannot be ordered, and we do not accept payments through the site. Listings,
          descriptions, availability and preview prices may change before ordering opens. A
          preview listing does not guarantee that a product will become available.
        </p>
        <h2>Using the website</h2>
        <p>
          You may browse the site for lawful personal use. Please do not interfere with the
          site, attempt unauthorised access, or submit malicious content. We may update or
          remove site content as the catalogue develops.
        </p>
        <h2>Product information</h2>
        <p>
          We aim to present product names and images accurately. Some details are still
          awaiting verification. Check the final product information, ingredients, warnings,
          eligibility, price and delivery terms when ordering becomes available. Do not rely
          on a preview listing as advice about whether a product is suitable for you.
        </p>
        <h2>Intellectual property</h2>
        <p>
          The site’s design, text and original artwork belong to Beautify Ourselves or are
          used with permission. Product names and trade marks belong to their respective
          owners. Product photographs are used with permission. Browsing this site does not
          grant a licence to reuse its content.
        </p>
        <h2>Site availability</h2>
        <p>
          We may change or temporarily interrupt the site. Nothing in these terms excludes
          or limits liability where doing so would be unlawful, or affects rights that
          cannot be excluded by law.
        </p>
        <h2>Changes to these terms</h2>
        <p>
          We may update these terms. The date above shows the latest version. Terms for
          placing orders will be published and made available before ordering opens.
        </p>
        <h2>Contact</h2>
        <p>
          Customer-care contact details will be published when they are confirmed and before
          ordering opens.
        </p>
      </article>
    </main>
  );
}

export function NotFoundPage() {
  return (
    <main id="main" className="content-page">
      <Meta
        title="Page not found | Beautify Ourselves"
        description="The requested Beautify Ourselves page could not be found."
        path="/404.html"
        noIndex
      />
      <div className="empty-state">
        <p className="eyebrow">Page not found</p>
        <h1>Let’s get you back to the collection.</h1>
        <Link className="button" to={routes.shop}>
          Explore categories
        </Link>
      </div>
    </main>
  );
}
